/**
 * useHarnClient — picks a HarnClient based on runtime selection + dev toggle.
 *
 * Modes:
 * - "gateway": routes to the runtime's openclaw gateway at httpUrl/harn/*
 *              using a Lexiun JWT from the instance token. Matches production.
 * - "direct":  routes to /harn-direct (Vite proxy → host-standalone harn-service
 *              at 127.0.0.1:3210). No auth. For harness dev loop without Docker.
 *
 * The direct mode is enabled when (any of):
 *   - localStorage.harn_mode === "direct"
 *   - URL query param ?harn=direct
 *
 * Toggle with `setHarnMode("direct" | "gateway")`.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { HarnClient } from "../lib/harn-client";
import { useRuntimeContext } from "./useRuntimeContext";

export type HarnMode = "gateway" | "direct";

const STORAGE_KEY = "harn_mode";

function readInitialMode(): HarnMode {
  if (typeof window === "undefined") return "gateway";
  const qs = new URLSearchParams(window.location.search);
  const q = qs.get("harn");
  if (q === "direct" || q === "gateway") return q;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "direct" || stored === "gateway") return stored;
  return "gateway";
}

export function useHarnMode(): [HarnMode, (m: HarnMode) => void] {
  const [mode, setMode] = useState<HarnMode>(readInitialMode);

  // Sync across tabs and after manual localStorage edits
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === "direct" || e.newValue === "gateway")) {
        setMode(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setModeAndPersist = useCallback((m: HarnMode) => {
    window.localStorage.setItem(STORAGE_KEY, m);
    setMode(m);
  }, []);

  return [mode, setModeAndPersist];
}

export interface UseHarnClientResult {
  client: HarnClient | null;
  /** Explains why `client` is null (for UI messaging). */
  unavailableReason: string | null;
  mode: HarnMode;
  setMode: (m: HarnMode) => void;
}

export function useHarnClient(): UseHarnClientResult {
  const runtime = useRuntimeContext();
  const [mode, setMode] = useHarnMode();

  const { client, unavailableReason } = useMemo<{
    client: HarnClient | null;
    unavailableReason: string | null;
  }>(() => {
    if (mode === "direct") {
      // Direct → Vite proxy at /harn-direct
      return {
        client: new HarnClient({ kind: "direct", baseUrl: "/harn-direct" }),
        unavailableReason: null,
      };
    }
    // Gateway mode: need a selected runtime target
    if (!runtime.target) {
      return {
        client: null,
        unavailableReason: "No runtime selected. Use the runtime picker to connect to a runtime.",
      };
    }
    return {
      client: new HarnClient({
        kind: "gateway",
        // Dev: route via Vite proxy to avoid CORS (gateway doesn't emit
        // Access-Control-* headers). Prod: hit the runtime FQDN directly.
        runtimeBaseUrl: import.meta.env.DEV ? "/harn-gateway-base" : runtime.target.httpUrl,
        getToken: async () => runtime.target?.harnToken ?? null,
      }),
      unavailableReason: null,
    };
  }, [mode, runtime.target]);

  return { client, unavailableReason, mode, setMode };
}
